import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { useOwnableAgents } from '@/hooks/useOwnableAgents.js';

interface Props {
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  /** Trigger id, so a `<Label htmlFor>` names the control. */
  id?: string;
  /** Set by a form's `FormControl`, so the trigger carries the field's error state and message. */
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

export function AgentSelect({
  value,
  onChange,
  disabled,
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'aria-required': ariaRequired,
}: Props) {
  const { t } = useTranslation();

  const { agents, isLoading, isError } = useOwnableAgents();

  if (isLoading) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder={t('common.loading')} />
        </SelectTrigger>
        <SelectContent />
      </Select>
    );
  }

  if (isError) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder={t('common.agentSelectError')} />
        </SelectTrigger>
        <SelectContent />
      </Select>
    );
  }

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        id={id}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-required={ariaRequired}
      >
        <SelectValue placeholder={t('members.agentPlaceholder')} />
      </SelectTrigger>
      <SelectContent>
        {agents.map((agent) => (
          <SelectItem key={agent.id} value={agent.id}>
            <span>
              {agent.name}
              {agent.tenantId ? (
                <span className="ml-1.5 text-muted-foreground text-xs">
                  {agent.tenantId}
                </span>
              ) : null}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
